import { TypographyH4 } from "@/components/typography/H4";
import { TypographySM } from "@/components/typography/SM";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { EmailNotificationCategory, Role } from "@/gql/__generated__/graphql";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useRef, useState } from "react";
import {
  TEST_MAIL_NAME_ATTRIBUTE,
  useHandleSubmit
} from "../hooks/handleSubmit";
import { ConfirmSend } from "./ConfirmSend";

const CATEGORIES: [string, string][] = Object.entries(
  EmailNotificationCategory
).map(([, v]) => [
  v,
  v.slice(0, 1) + v.slice(1).toLocaleLowerCase().replaceAll("_", " ")
]);

const USER_MAP: [string, Role][] = [
  ["All users", Role.User],
  ["Admins only", Role.Admin]
];

const InternalMailPlatform = () => {
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [openConfirmSend, setOpenConfirmSend] = useState(false);
  const { handleSubmit, loading } = useHandleSubmit();

  const formRef = useRef<HTMLFormElement>(null);

  return (
    <div className="w-full m-4 mt-[68px] flex flex-col items-center justify-center">
      <Link to="/organization" className="absolute top-4 left-4">
        <Button variant="secondary" className="gap-2 w-fit">
          <ArrowLeft size={14} />
          To Numerous platform
        </Button>
      </Link>
      <div className="w-1/2 mb-10 max-w-lg">
        <TypographyH4>Send email to Numerous users</TypographyH4>
        <TypographySM className="dark:text-brand-gray-200">
          Use the form below to send an email to some, or all, Numerous users.
          <br />
          Be aware that sending emails through this form does not support
          dynamic content in the SendGrid template.
          <br />
          <br />
          <span className="font-semibold">
            NOTE: Remember to set the email subject on the SendGrid template.
          </span>
        </TypographySM>
      </div>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        onChange={(e) => setDisableSubmit(!e.currentTarget.checkValidity())}
        className="flex flex-col w-1/2 max-w-lg gap-y-2"
      >
        <Label htmlFor="sendGridTemplateID">SendGrid template ID</Label>
        <Input name="sendGridTemplateID" required type="text" />

        <Label className="mt-5" htmlFor="emailCategory">
          Email category
        </Label>
        <Select name="emailCategory" required>
          <SelectTrigger>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Label className="mt-5" htmlFor="userType">
          Recepients
        </Label>
        <Select name="userType" required>
          <SelectTrigger>
            <SelectValue placeholder="Select user type" />
          </SelectTrigger>
          <SelectContent>
            {USER_MAP.map(([k, v]) => (
              <SelectItem key={k} value={v}>
                {k}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="mt-12 flex justify-between gap-6">
          <Button
            variant="secondary"
            className="w-full"
            type="submit"
            name={TEST_MAIL_NAME_ATTRIBUTE}
            disabled={disableSubmit}
          >
            Send test to self
          </Button>
          <Button
            className="w-full"
            type="button"
            onClick={() => setOpenConfirmSend(true)}
            disabled={disableSubmit}
          >
            Send email to users
          </Button>
        </div>

        <div
          className={cn(
            "mt-6 flex items-center justify-center gap-2 text-brand-warning",
            !loading && "invisible"
          )}
        >
          <LoadingCircle type="warning" />
          <TypographySM>Sending platform mail</TypographySM>
        </div>

        <AnimatePresence>
          {openConfirmSend && (
            <ConfirmSend
              onOpenChange={() => setOpenConfirmSend(false)}
              onConfirm={() => formRef.current?.requestSubmit()}
            />
          )}
        </AnimatePresence>
      </form>
    </div>
  );
};

export default InternalMailPlatform;
